/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

enum JobControlOptions {
  sync_execute = 'sync-execute',
  async_execute = 'async-execute',
  dismiss = 'dismiss'
}

export enum linksDefinition {
  documentation = 'service-doc',
  openApi = 'service-desc',
  conformance = 'http://www.opengis.net/def/rel/ogc/1.0/conformance',
  processes = 'http://www.opengis.net/def/rel/ogc/1.0/processes',
  process = 'process',
  processPackage = 'package',
  jobResults = 'results',
  jobMonitor = 'monitor',
  project = 'project',
  self = 'self',
  execution = 'execution',
  stacItem = 'stac_item',
  stacBrowser = 'stac_browser',
  provider = 'source',
  logs = 'logs',
  editlink = 'edit-link',
  permalink = 'permalink'
}
interface TransmissionMode {
  value: 'value';
  reference: 'reference';
}

interface Metadata {
  title?: string;
  role?: string;
  href?: string;
}

export interface Link {
  href: string;
  rel: string;
  type?: string;
  hreflang?: string;
  title?: string;
}

interface AdditionalParameter {
  name: string;
  value: string | number | Array<unknown> | Record<string, unknown>;
}

interface AdditionalParameters extends Metadata {
  parameters?: AdditionalParameter[];
}

interface SchemaItem {
  title?: string;
  required?: string;
  type?: string;
  description?: string;
  format?: string;
  default?: unknown;
  enum?: Array<string | number | boolean>;
  nullable?: boolean;
  contentMediaType?: string;
  contentEncoding?: string;
  properties?: Record<string, SchemaItem>;
  minItems?: number;
  maxItems?: number;
  minimum?: number;
  maximum?: number;
}

interface DescriptionType {
  title?: string;
  description?: string;
  keywords?: string[];
  metadata?: Metadata[];
  additionalParameters: AdditionalParameters;
}

export interface InputDescription extends DescriptionType {
  minOccurs?: number;
  maxOccurs?: number | 'unbounded';
  schema: SchemaItem;
}

export interface OutputDescription extends DescriptionType {
  schema: SchemaItem;
}

export interface Conformance {
  conformsTo: string;
}

export interface ProcessSummary extends DescriptionType {
  id: string;
  version: string;
  jobControlOptions?: JobControlOptions[];
  outputTransmission?: TransmissionMode[];
  links?: Link[];
}

export interface ProcessList {
  processes: ProcessSummary[];
  links: Link[];
}

export interface ProcessDescription extends ProcessSummary {
  inputs?: Record<string, InputDescription>;
  outputs?: Record<string, OutputDescription>;
}

export interface ProcessPackage {
  main: string;
  source: string;
  content: string;
}

export type ProgressionStateValue = {
  order: number;
  jobs: ProgressionStateItem[];
};

export type ProgressionStateItem = {
  status: 'pending' | 'running' | 'failed' | 'completed';
  started?: string; // ISO timestamp
  finished?: string; // ISO timestamp
  name: string; // Label for the step
  message?: string; // Optional message for the step
};

export const JobStatusEnum = ['running', 'accepted', 'dismissed', 'failed', 'successful'] as const;
export interface Job {
  asset_urls?: string[]; // Array of URLs for assets
  commit_ref: string;
  execution_content?: Record<string, unknown>;
  finished?: string; // ISO timestamp
  jobID: string;
  processID: string;
  type: string;
  status: (typeof JobStatusEnum)[number]; // e.g., "failed", "success", etc.
  message: string;
  created: string; // ISO timestamp
  started: string; // ISO timestamp

  assigned_at?: string;
  assigned_to?: string;

  updated?: string; // ISO timestamp
  progress: number; // Progress percentage (e.g., 0-100)
  links: Link[]; // Array of link objects
  project_path?: string;
  project_ref?: string;

  result_url?: string;

  log_urls?: string[]; // Array of log URLs
  progression_state?: Record<string, ProgressionStateValue>; // Progression state of the job
  cwl?: string;
  env?: Record<string, string>;
  executionID?: string;
}

export type JobExtended<T> = Job & T;
export interface JobList {
  jobs: Job[];
  links: Link[];
}

export interface JobResults {
  detail: string[];
}

export enum TypeProcessing {
  OgcProcess = 'OgcProcess',
  ZeroProcess = 'ZeroProcess'
}
